import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, skipWhile, Subscription } from 'rxjs';
import { langDe } from 'src/app/translations/de';
import { langEn } from 'src/app/translations/en';
import { langEs } from 'src/app/translations/es';
import { langFr } from 'src/app/translations/fr';
import { langIt } from 'src/app/translations/it';

import { AppStartupService } from '../../shared/app-startup.service';
import { Page, PersistentMenuItem, PersistentMenuItemType } from '../../shared/page';
import { SocketService } from '../../shared/socket.service';
import { CustomizationService } from '../customization/services/customization.service';
import { ChatConversation, ChatHeader, ClosedConversation, InMessageLanguageEnum } from '../customization/shared';
import { UploadMonitor } from './file-upload/upload-monitor';
import { MessageService } from './services/message.service';
import { Button, Conversation, InMessageFile, Message, MessageTypeEnum, TextInput, TextInputType } from './shared';
import { TextareaAutoExpandDirective } from './textarea-auto-expand.directive';

const MESSAGES_PAGE_SIZE = 10;
const TYPING_INDICATOR_TIMEOUT_SEC = 60;

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChatComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  pageInfo: Page;
  showSpinner = false;
  showTypingIndicator = false;
  private typingIndicatorTimeoutId: any;

  // the messages displayed in the chat window
  messages: Message[] = [];

  /*
   * Because we might have missed messages since we last called GET /conversation
   * (for example if the websocket is currently disconnected for some reason),
   * and because we need to fetch any new image messages from the backend to replace its progress bar,
   * we always need to fetch from (excluding) the last message seen from a GET request
   */
  private reliablyNewestMessageId = 0; // we need something for the first message ever posted by a given user

  // the message currently typed
  messageContent: string;
  inputIsValid = true;
  phoneInputIsPristine = true;
  phoneInputValue: string;
  phoneObj: any;

  // input placeholder text and type
  inputPlaceholder = '';
  inputType: string;

  // Let's not try to scroll ervery time ngAfterViewChecked is called
  private shouldScrollToBottom = false;

  // We need to prevent posting and refreshing at the same time
  private currentlyPostingMessage = false;
  private shouldRefreshAfterPosting = false;

  // On pages that configure persistent menu to be open by default, do not close it when an item is clicked
  private persistenMenuOpenByDefault = false;

  // If the last displayed message is of type text_input,
  // we need to change the input type and display a custom label
  applicableTextInput: TextInput;

  // If the last displayed brand message is a QR with a special checkbox checked will be set to true
  hideInput = true;

  // Will contain the privacy link
  privacyLinkText: string;

  // Set to true if a suitable message is received from the parent
  isMobile = false;
  standalone = false;

  /**
   * Flag to show the watermark for Care Essential companies.
   */
  showWatermark = false;

  // waiting time and position info if enabled in account settings
  enableWaitingTime = false;
  waitingInfo = {
    time: '0',
    position: 0,
  };

  // Will be set to false when chat window has finished loading - controlled by the parent
  @Input() isLoading: boolean;

  // reference to the messages container div
  // (we want to scroll it down when appropriate,
  //  and detect when it is scrolled all the way up)
  @ViewChild('conversation') conversationContainer: ElementRef;

  // reference to the inputs, so we can unfocus them when the user touches the conversation
  @ViewChild('messageInput') messageInputElement: ElementRef;
  @ViewChild('phoneInput') phoneInputElement: ElementRef;
  @ViewChild(TextareaAutoExpandDirective) textareaDirective: TextareaAutoExpandDirective;

  private subscriptions = new Subscription();

  constructor(
    private socketService: SocketService,
    private messageService: MessageService,
    private appStartupService: AppStartupService,
    private customizationService: CustomizationService,
    private translateService: TranslateService
  ) {
    this.listenCustomizationAndRefresh();
  }

  /**
   * Once the relevant conditions are met (see page service for more details),
   * load the page information, then optionnaly tell the back-end to start a new conversation,
   * then load existing messages.
   */
  ngOnInit(): void {
    this.appStartupService.initApp();
    this.appStartupService.pageInfo$.subscribe(pageInfo => {
      this.pageInfo = pageInfo;
      this.showWatermark = pageInfo.enable_watermark;
      this.enableWaitingTime = pageInfo.enable_waiting_time;

      // We need a jwt for the websocket endpoints
      this.initIoConnection();
    });
    this.appStartupService.initialConversation$.subscribe((conversation: Conversation) => {
      const messages = conversation?.messages || [];
      this.messages = messages;
      this.shouldScrollToBottom = true;
      this.updateReliablyNewestMessage(messages);
      this.updateInputType();
      this.standalone = this.appStartupService.isStandaloneMode();
      if (this.standalone) {
        this.fetchOlderMessagesToFillView(messages);
      }
      this.scrollToBottom();
    });
  }

  private listenCustomizationAndRefresh() {
    this.subscriptions.add(
      this.customizationService.chatCustomizationBS
        .pipe(skipWhile(i => !i))
        .subscribe(
          ({
            mbr,
            chatHeaderData,
            chatConversationData,
          }: {
            mbr: ClosedConversation;
            chatHeaderData: ChatHeader;
            chatConversationData: ChatConversation;
          }) => {
            if (chatHeaderData) {
              if (this.customizationService.chatHeaderStyleCustomized) {
                if (document.body.contains(this.customizationService.chatHeaderStyleCustomized)) {
                  document.body.removeChild(this.customizationService.chatHeaderStyleCustomized);
                }
              }
              const { pageLanguage } = this.customizationService;
              const { css, additionalCSS, title, subtitle, privacyLinkPrefix, privacyLinkLabel, privacyLink } =
                chatHeaderData;
              this.translateService.use(pageLanguage);
              let translations = null;
              switch (pageLanguage) {
                case InMessageLanguageEnum.FRENCH:
                  translations = langFr;
                  break;
                case InMessageLanguageEnum.ENGLISH:
                  translations = langEn;
                  break;
                case InMessageLanguageEnum.ITALIAN:
                  translations = langIt;
                  break;
                case InMessageLanguageEnum.GERMAN:
                  translations = langDe;
                  break;
                case InMessageLanguageEnum.SPANISH:
                  translations = langEs;
                  break;
                default:
                  translations = langEn;
                  break;
              }
              const defaultTranslations = { ...translations['default'] };
              this.translateService.setTranslation(pageLanguage, defaultTranslations);
              const updateTranslationKey = (key: string, value: string) =>
                this.translateService.set(key, value || defaultTranslations[key]);

              updateTranslationKey('title', title);
              updateTranslationKey('subtitle', subtitle);
              updateTranslationKey('privacy_link_prefix', privacyLinkPrefix);
              updateTranslationKey('privacy_link_label', privacyLinkLabel);
              this.customizationService.chatHeaderStyleCustomized = document.createElement('style');
              const fullCSS = `${css}
${additionalCSS}`;
              this.customizationService.chatHeaderStyleCustomized.innerHTML = fullCSS;
              document.body.appendChild(this.customizationService.chatHeaderStyleCustomized);
              if (this.pageInfo) {
                this.pageInfo.privacy_link = privacyLink;
              }

              if (chatConversationData) {
                if (this.customizationService.chatConversationStyleCustomized) {
                  if (document.body.contains(this.customizationService.chatConversationStyleCustomized)) {
                    document.body.removeChild(this.customizationService.chatConversationStyleCustomized);
                  }
                }
                const { css, additionalCSS, inputMailLabel, inputPhoneLabel, inputTextLabel, selectOptionLabel } =
                  chatConversationData;
                updateTranslationKey('send_your_email', inputMailLabel);
                updateTranslationKey('send_your_phone', inputPhoneLabel);
                updateTranslationKey('send_a_message', inputTextLabel);
                updateTranslationKey('select_an_option', selectOptionLabel);
                this.customizationService.chatConversationStyleCustomized = document.createElement('style');
                const fullCSS = `${css}
${additionalCSS}`;
                this.customizationService.chatConversationStyleCustomized.innerHTML = fullCSS;
                document.body.appendChild(this.customizationService.chatConversationStyleCustomized);
              }
              if (mbr) {
                if (this.customizationService.closedConversationStyleCustomized) {
                  if (document.body.contains(this.customizationService.closedConversationStyleCustomized)) {
                    document.body.removeChild(this.customizationService.closedConversationStyleCustomized);
                  }
                }
                const { css, additionalCSS } = mbr;
                this.customizationService.closedConversationStyleCustomized = document.createElement('style');
                const fullCSS = `${css}
${additionalCSS}`;
                this.customizationService.closedConversationStyleCustomized.innerHTML = fullCSS;
                document.body.appendChild(this.customizationService.closedConversationStyleCustomized);
              }
            }
          }
        )
    );
  }

  /**
   * Hook detection of scroll-to-top;
   * hook scroll to bottom when the frame becomes
   */
  ngAfterViewInit() {
    const container = this.conversationContainer.nativeElement;
    container.addEventListener('scroll', () => {
      // If there is 10 messages or more, and if the user scrolled to the top,
      // let's fetch ten more messages
      if (
        this.messages.length >= MESSAGES_PAGE_SIZE &&
        container.scrollHeight > container.offsetHeight &&
        container.scrollTop === 0
      ) {
        this.fetchOlderMessages(true); // adjust scroll afterwards so that the displayed messages stay were they were
      }
    });

    window.addEventListener(
      'message',
      event => {
        if (event.data.type === 'becomesVisible') {
          // scroll to bottom doesn't work while the frame is hidden with display: none,
          // because elements don't have heights in that case.
          // Hence we need to scroll down "manually" when the outer page informs us
          // that the visiblity has changed
          this.scrollToBottom();
          this.fetchOlderMessagesToFillView(this.messages);
          if (event.data.isMobile) {
            // Adds some extra styling in case of mobile
            this.isMobile = true;
          }
          if (event.data.isMenuInitiallyOpen) {
            this.persistenMenuOpenByDefault = true;
            this.showPersistentMenu(); // note that the menu's markup still won't be displayed if the page doesn't have a menu
          }

          window.parent.postMessage({ type: 'newMessageNotif', isVisible: false }, '*');
        }
      },
      false
    );

    // Close the chat window on 'x' btn click
    document
      .querySelector('.close-btn')
      .addEventListener('click', () => window.parent.postMessage({ type: 'hideFrame' }, '*'));
  }

  /**
   * Unfocus the currently focused input, if any
   * This was originally requested by CoopVoce,
   * as iOS safari doesn't unfocus the input on its own (desktop Chrome does, at least)
   */
  unfocusInputs() {
    if (this.messageInputElement) {
      this.messageInputElement.nativeElement.blur();
    }
  }

  /**
   * Scroll the messages pane down if the array of messages have changed
   */
  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  /**
   * Sends the text currently in the input field to the backend, and empties the field
   */
  sendTextMessage() {
    if (this.messageContent) {
      this.sendMessage(Message.newTextMessage(this.messageContent));
      this.messageContent = '';
      setTimeout(() => this.textareaDirective?.updateRows()); // give angular a chance to update the texarea value
    }
  }

  checkAndSendInputMessage(inputRe: RegExp) {
    if (this.messageContent && this.messageContent.match(inputRe)) {
      this.inputIsValid = true;
      this.sendMessage(Message.newTextInputResponseMessage(this.applicableTextInput, this.messageContent));
      this.applicableTextInput = null;
      this.messageContent = '';
    } else {
      this.inputIsValid = false;
    }
  }

  sendEmailInputMessage() {
    const emailRE =
      // eslint-disable-next-line no-useless-escape
      /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    this.checkAndSendInputMessage(emailRE);
  }

  sendPhoneInputMessage() {
    this.phoneInputIsPristine = false;
    if (this.isPhoneInputValid()) {
      this.sendMessage(Message.newTextInputResponseMessage(this.applicableTextInput, this.phoneInputValue));
      this.phoneInputValue = '';
      this.phoneInputIsPristine = true;
      this.applicableTextInput = null;
    }
  }

  enterPressedInTextarea(event) {
    if (!event.shiftKey) {
      event.preventDefault(); // prevent a linebreak from being inserted in the textarea when the enter key fires a keyup event
      this.sendTextMessage();
    }
  }

  sendInputMessage() {
    if (this.isRegularInput()) {
      this.sendTextMessage();
    } else if (this.isEmailInput()) {
      this.sendEmailInputMessage();
    } else if (this.isPhoneInput()) {
      setTimeout(() => this.sendPhoneInputMessage());
    }
  }

  /**
   * Tells whether a special message of type TextInput triggered displaying an email input field
   */
  isEmailInput() {
    return this.applicableTextInput && this.applicableTextInput.type === TextInputType.EMAIL;
  }

  /**
   * Tells whether a special message of type TextInput triggered displaying an phone input field
   */
  isPhoneInput() {
    return this.applicableTextInput && this.applicableTextInput.type === TextInputType.PHONE;
  }

  /**
   * Tells whether no special message of type TextInput is currently applicable
   */
  isRegularInput() {
    return !this.applicableTextInput;
  }

  phoneInputChanged(event) {
    this.phoneInputValue = event;
  }

  phoneInputEnterPressed() {
    this.phoneInputElement.nativeElement.blur();
    setTimeout(() => this.sendPhoneInputMessage());
  }

  telInputObject(obj) {
    this.phoneObj = obj;
  }

  isPhoneInputValid() {
    return this.phoneObj?.isValidNumber();
  }

  isImageFileMessage(message: Message) {
    return message.file.mime_type?.startsWith('image');
  }

  isVideoFileMessage(message: Message) {
    return message.file.mime_type?.startsWith('video');
  }

  /**
   * Called when the user selects a file to upload.
   * Triggers the upload, inserts a progress bar message in the chat,
   * and upon upload completion POSTs a picture-type message to the backend
   */
  fileChosen(monitor: UploadMonitor) {
    const message = Message.newUploadMonitorMessage(monitor);
    monitor.progress.subscribe({
      complete: () => {
        this.fileUploaded(monitor.file);
      },
    });
    this.messages.push(message);
  }

  pageHasPersistentMenu() {
    return this.pageInfo && this.pageInfo.persistent_menu.length > 0;
  }

  showPersistentMenu() {
    const persistentMenuElement = document.getElementById('persistent-menu');
    if (persistentMenuElement) {
      persistentMenuElement.classList.remove('dismiss');
      persistentMenuElement.classList.add('visible');
    }
  }

  closePersistentMenu() {
    const persistentMenuElement = document.getElementById('persistent-menu');
    if (persistentMenuElement) {
      persistentMenuElement.classList.remove('visible');
      persistentMenuElement.classList.add('dismiss');
    }
  }

  menuItemClicked(item: PersistentMenuItem) {
    switch (item.type) {
      case PersistentMenuItemType.Restart:
        this.messageService.postMessage(Message.newStartConversationMessage()).subscribe();
        break;
      case PersistentMenuItemType.Url:
        window.open(item.urlOrPayload, '_blank');
        break;
      case PersistentMenuItemType.Postback:
        this.messageService.postMessage(Message.newPostbackMessage(item.title, item.urlOrPayload)).subscribe();
        break;
      case PersistentMenuItemType.Transcript:
        this.messageService.downloadTranscript().subscribe();
        break;
    }
    if (!this.persistenMenuOpenByDefault) {
      this.closePersistentMenu();
    }
  }

  /**
   * POSTs the given message to the backend, updating its id from the response
   */
  private sendMessage(message: Message): void {
    this.currentlyPostingMessage = true;
    this.messageService.postMessage(message).subscribe(result => {
      message.id = result.id;
      if (message.isImage()) {
        // let's start with a 60vw by 60vw square empty image
        this.messageService.injectImageURL(message.image);
      }
      this.shouldScrollToBottom = true;
      this.currentlyPostingMessage = false;
      this.messages.push(message);

      // If we got a refresh notification while posting,
      // the refresh is deferred to here because we need to pass the message id
      // of the just-posted message as a parameter of the refresh endpoint
      if (this.shouldRefreshAfterPosting) {
        this.shouldRefreshAfterPosting = false;
        this.fetchNewerMessages();
      }
    });
  }

  /**
   * Called when a file has been uploaded. POSTs a file message to the backend
   */
  private fileUploaded(file: InMessageFile) {
    this.sendMessage(Message.newFileMessage(file.uuid));
  }

  /**
   * called when the user clicks a button.
   * Forwards the payload to the /click endpoint
   */
  buttonClicked(button: Button, message: Message) {
    if (message.type === MessageTypeEnum.CAROUSEL) {
      if (!button['buttonAlreadyClicked']) {
        button.name = button['text'];
        button['buttonAlreadyClicked'] = true;
        this.sendMessage(Message.newButtonMessage(button));
      }
    } else if (!message.buttonAlreadyClicked) {
      message.buttonAlreadyClicked = true;
      this.sendMessage(Message.newButtonMessage(button));
    }
  }

  /**
   * Have the websocket connected to the server, and subscribe to new-messages-in-conversation notifications
   */
  private initIoConnection(): void {
    this.socketService.init();
    this.socketService.subscribeToRefreshNotifications((messageEvent: MessageEvent) => {
      this.processMessageNotification(messageEvent);
    });
  }

  private processMessageNotification(messageEvent: MessageEvent) {
    let eventType = null;
    let eventData = null;

    if (messageEvent?.data) {
      eventData = JSON.parse(messageEvent.data);
      eventType = eventData.data?.type;
    }

    if (eventType === 'typing-start') {
      this.startTypingIndicator();
      this.hideWaitingInfo();
    } else if (eventType === 'typing-end') {
      this.stopTypingIndicator();
    } else if (eventType === 'waiting-info') {
      // the event is sent only if enable_waiting_time is true, so no need "if" here
      this.waitingInfo.position = eventData.data.position_in_queue;
      this.waitingInfo.time = this.computeWaitingTime(eventData.data.personal_wait_time);

      if (this.waitingInfo.position > 0) {
        this.scrollToBottom();
      }
    } else if (eventType === 'file') {
      if (eventData.data.file_uuid) {
        const file = new InMessageFile(eventData.data.file_uuid);
        this.messageService.injectFileURL(file);
        window.open(file.url, '_blank');
      }
    }
    // The websocket is telling us we should refresh messages,
    // but if we are currently posting a message,
    // we need to wait until the server returned the newly created message id
    // (because we'll need the id to merge the refreshed messages)
    else if (this.currentlyPostingMessage) {
      this.shouldRefreshAfterPosting = true;
    } else {
      this.fetchNewerMessages();
    }
  }

  private startTypingIndicator() {
    this.showTypingIndicator = true;

    // let's scroll down when typing indicator arrive even if the user has scrolled up to see past messages (same behavior as new message)
    setTimeout(() => {
      this.scrollToBottom();
    }, 0); // allow the DOM to update

    if (this.typingIndicatorTimeoutId) {
      clearTimeout(this.typingIndicatorTimeoutId);
    }
    this.typingIndicatorTimeoutId = setTimeout(() => {
      this.showTypingIndicator = false;
    }, TYPING_INDICATOR_TIMEOUT_SEC * 1000);
  }

  private stopTypingIndicator() {
    this.showTypingIndicator = false;
    if (this.typingIndicatorTimeoutId) {
      clearTimeout(this.typingIndicatorTimeoutId);
    }
  }

  private fetchOlderMessagesToFillView(lastFetchedMessages) {
    window.setTimeout(() => {
      this.scrollToBottom();
      const container = this.conversationContainer.nativeElement;
      if (lastFetchedMessages.length === MESSAGES_PAGE_SIZE && container.scrollHeight === container.offsetHeight) {
        console.log('fetching more messages to fill view');
        this.fetchOlderMessages(false).then(olderMessages => this.fetchOlderMessagesToFillView(olderMessages));
      }
    }, 0); // allow the DOM to update
  }

  /**
   * Fetch 10 older messages from the server when the user scrolls to the top
   */
  private fetchOlderMessages(adjustScrollAfterwards: boolean) {
    let resolveFn = null;
    const promise = new Promise<Message[]>(resolve => (resolveFn = resolve));
    const currentOldestId = this.messages[0].id;
    this.showSpinner = true;
    const offsetOfLastMessage = this.offsetOfLastMessage();
    this.messageService.getOlderMessages(currentOldestId).subscribe(evenOlderMessages => {
      this.messages = evenOlderMessages.concat(this.messages);
      this.showSpinner = false;
      resolveFn(evenOlderMessages);
      if (adjustScrollAfterwards) {
        this.adjustScroll(offsetOfLastMessage);
      }
    });
    return promise;
  }

  private adjustScroll(previousOffsetOfLastMessage) {
    setTimeout(() => {
      const scrollTop = this.offsetOfLastMessage() - previousOffsetOfLastMessage;
      this.conversationContainer.nativeElement.style['scroll-behavior'] = 'auto';
      this.conversationContainer.nativeElement.scrollTop = scrollTop;
      this.conversationContainer.nativeElement.style['scroll-behavior'] = 'smooth';
    }, 0);
  }

  private offsetOfLastMessage() {
    const messages = this.conversationContainer.nativeElement.querySelectorAll('.alc-message');
    if (!messages?.length) {
      return 0;
    }
    return messages[messages.length - 1].offsetTop;
  }

  /**
   * Fetch from the server all messages that are newer than our currently newest message
   */
  private fetchNewerMessages() {
    this.messageService.getNewerMessages(this.reliablyNewestMessageId).subscribe(newerMessages => {
      if (newerMessages.length > 0) {
        this.socketService.resetExponentialBackoff();
        // let's scroll down when new messages arrive even if the user has scrolled up to see past messages
        this.stopTypingIndicator();
        this.shouldScrollToBottom = true;

        if (newerMessages.some(message => message.is_from_brand)) {
          window.parent.postMessage({ type: 'newMessageNotif', isVisible: true }, '*');
          this.hideWaitingInfo();
        }
      }

      this.removeCompletedProgressBars();
      this.mergeNewerMessages(newerMessages);
      this.updateReliablyNewestMessage(newerMessages);
      this.updateInputType();
    });
  }

  /**
   * Updates the id of the message up to which we are sure not to have missed any message
   */
  private updateReliablyNewestMessage(messages: Message[]) {
    if (messages && messages.length > 0) {
      const latest = messages[messages.length - 1];
      if (latest.id > this.reliablyNewestMessageId) {
        this.reliablyNewestMessageId = latest.id;
      }
    }
  }

  /**
   * Updates the type of input we offer to the user (regular + image or email at the moment).
   * Special inputs are triggered by messages of type 'text_input' from the brand,
   * which are not displayed but instead change type of the displayed input (and hide the pix upload button)
   */
  private updateInputType() {
    const lastMessage = this.messages.slice(-1)[0];
    if (lastMessage && lastMessage.type === MessageTypeEnum.TEXT_INPUT) {
      this.applicableTextInput = lastMessage.text_input;

      if (this.isEmailInput()) {
        this.inputPlaceholder = 'send_your_email';
        this.inputType = 'email';
      } else if (this.isPhoneInput()) {
        this.inputPlaceholder = 'send_your_phone';
        this.inputType = 'tel';
      }
    } else {
      this.applicableTextInput = null;
      this.inputPlaceholder = 'send_a_message';
      this.inputType = 'text';
    }

    const lastMessageFromBrand = [...this.messages].reverse().find(message => message.is_from_brand);
    if (lastMessageFromBrand && lastMessageFromBrand.disable_input) {
      this.hideInput = true;
      this.messageInputElement?.nativeElement.blur();
      this.inputPlaceholder = 'select_an_option';
    } else {
      this.hideInput = false;
    }
  }

  /**
   * Trims any completed progress bars from the array of messages
   */
  private removeCompletedProgressBars() {
    for (let i = 0; i < this.messages.length /* incremented below */; ) {
      const message = this.messages[i];
      if (message.isProgressBar && message.isProgressBar() && message.monitor.uploadComplete) {
        this.messages.splice(i, 1);
      } else {
        i++;
      }
    }
  }

  /**
   * Merges recent messages retreived via a GET /conversations?min_message_id= from the backend.
   * Any message that we already have in our array is overwritten (in case the server modified it);
   * messages that we didn't have before are inserted where they belong.
   *
   * @param newestIdFromBrand the message id we used as min_message_id parameter to GET /conversations
   *         (note that it might not be the newest any more,
   *          for example if fetchNewerMessages has been called again before its GET request completed)
   * @param newerMessages the messages received from the backend, to be merged into this.messages
   */
  private mergeNewerMessages(newerMessages: Message[]): void {
    // example scenario:
    //                    0     1     2     3           4     5     6     7     8
    // current messages id1-S id2-B id3-B id4-S       id6-S progr progr id7-S undef-S
    // newer messages                     id4-S id5-B id6-S             id7-S
    //                                      0     1     2                 3

    let currentMessagesIndex = 0;
    let newerMessagesIndex = 0;

    // we loop until we reach the end of either arrays
    while (currentMessagesIndex < this.messages.length && newerMessagesIndex < newerMessages.length) {
      const currentCurrentMessage = this.messages[currentMessagesIndex];
      const currentNewerMessage = newerMessages[newerMessagesIndex];

      if (currentCurrentMessage.id === undefined) {
        // Could be an not-yet-completed progress bar, a freshly posted message that didn't get an id yet;
        // in anycase, leave it where it is
        currentMessagesIndex++;
      } else if (currentCurrentMessage.id < currentNewerMessage.id) {
        // current messages index needs to catch up on newer messages
        currentMessagesIndex++;
      } else if (currentCurrentMessage.id === currentNewerMessage.id) {
        // if ids are equal, we overwrite the current message with the one
        // just returned from the server, in case the server modified the message,
        // with URL shortening for example
        this.messages[currentMessagesIndex++] = newerMessages[newerMessagesIndex++];
      } else {
        // currentCurrentMessage.id > currentNewerMessage.id

        // currentCurrentMessage has overtaken the newer messages:
        // we need to insert the current new message
        this.messages.splice(currentMessagesIndex++, 0, newerMessages[newerMessagesIndex++]);
        // Note that currentMessagesIndex was incremented above
        // but still points to the same element,
        // as the item insertion has shifted the remaining items' indexes
      }
    }
    // At the end of the loop, if we have reached the end of currentMessages
    // while there still are newer messages, we need to append them at the end of the current messages.
    // We know that the remaining ones are more recent, because we only bump currentMessageIndex
    // when the currentCurrentId is smaller or equal to the currentNewerId
    // (or when currentCurent doesn't have an id)
    while (newerMessagesIndex < newerMessages.length) {
      this.messages.push(newerMessages[newerMessagesIndex++]);
    }
  }

  // thanks https://stackoverflow.com/questions/35232731/angular2-scroll-to-bottom-chat-style
  private scrollToBottom(): void {
    const container = this.conversationContainer?.nativeElement;
    if (container) {
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 300);
    }
  }

  private hideWaitingInfo() {
    if (this.enableWaitingTime) {
      this.waitingInfo.position = 0;
    }
  }

  private computeWaitingTime(seconds: number): string {
    const timeInMin = seconds / 60;
    const lessThan = this.translateService.instant('lessThan');
    const moreThan = this.translateService.instant('moreThan');
    const minutes = this.translateService.instant('minutes');

    if (timeInMin <= 5) {
      return `${lessThan} 5 ${minutes}`;
    } else if (timeInMin <= 10) {
      return `${lessThan} 10 ${minutes}`;
    } else if (timeInMin <= 15) {
      return `${lessThan} 15 ${minutes}`;
    } else {
      return `${moreThan} 15 ${minutes}`;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
